import urlJoin from 'url-join';
import { AppSettings } from 'core/AppSettings';

export const HUB_SOURCE_PARAM = 'ideagen_hub_source';
export const HUB_LOGIN_PATH = '/security/hub-login';
const RETURN_URL_PARAM = 'ReturnUrl';

/**
 * True when the current URL carries the Hub tile marker, indicating the SPA was opened from an
 * Ideagen Hub tile and should authenticate via the Hub sign-in entry point rather than the normal
 * login funnel.
 */
export function hasHubSourceMarker(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get(HUB_SOURCE_PARAM)?.toLowerCase() === 'true';
}

/**
 * Builds the redirect target for the Hub sign-in entry point, carrying the marker and a validated
 * ReturnUrl back to the current page (with the marker stripped, so a later session expiry on the
 * landed page takes the normal login path). The ReturnUrl must be absolute — the backend's
 * RedirectUrlValidator only trusts a ReturnUrl whose parsed host matches the request host or its
 * whitelist, and treats anything non-absolute as invalid.
 *
 * The entry point lives on the API host (`AppSettings.cumulusApiUrl`), not the omnis SPA origin —
 * omnis-test is a pure static SPA/CDN host that serves every path as index.html, so a same-origin
 * hub-login URL would never reach the backend and would just re-render the SPA (an infinite bounce).
 *
 * Anti-nesting guard: a hub-login URL must never appear inside a ReturnUrl. If the SPA is (wrongly)
 * served at the hub-login path itself — on either host — redirecting here again with the current URL
 * as ReturnUrl would nest indefinitely. Instead we unwrap: reuse the ReturnUrl already present on the
 * current URL (or fall back to the origin), so repeated calls are idempotent rather than
 * ever-deepening.
 */
export function getHubLoginUrl(): string {
  const returnUrl = getReturnUrl();
  return `${urlJoin(getHubLoginHost(), HUB_LOGIN_PATH)}?${HUB_SOURCE_PARAM}=true&ReturnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * Defends against a config regression dropping the scheme off `cumulusApiUrl` (its Octopus token is
 * a bare host, so this is one composition step away from ever being wrong again) — without it,
 * `window.location.href = <scheme-less string>` is treated as a relative path by the browser and the
 * SPA loop returns.
 */
function getHubLoginHost(): string {
  const host = AppSettings.cumulusApiUrl;
  return host.startsWith('http') ? host : `https://${host}`;
}

function getReturnUrl(): string {
  const candidate = buildCleanUrl();
  if (isHubLoginPath(new URL(candidate).pathname)) {
    return getExistingReturnUrl();
  }
  return candidate;
}

function buildCleanUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(HUB_SOURCE_PARAM);
  const search = url.searchParams.toString();
  return `${url.origin}${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
}

function getExistingReturnUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const existing = params.get(RETURN_URL_PARAM);
  return existing || window.location.origin;
}

function isHubLoginPath(pathname: string): boolean {
  return pathname.toLowerCase() === HUB_LOGIN_PATH;
}
